import { z } from "zod/v4";

export const jurisdictionLevels = ["national", "provincial", "municipal"];
export const normTypes = [
  "constitution",
  "international_treaty",
  "law",
  "decree",
  "resolution",
  "disposition",
  "judicial_agreement",
  "regulation",
  "ordinance",
  "other",
];
export const normStatuses = ["active", "partially_repealed", "repealed", "suspended", "unknown"];
export const legalUnitTypes = [
  "part",
  "book",
  "title",
  "chapter",
  "section",
  "article",
  "subsection",
  "paragraph",
  "annex",
  "other",
];
export const relationshipTypes = [
  "modifies",
  "repeals",
  "partially_repeals",
  "regulates",
  "complements",
  "extends",
  "suspends",
  "restores",
  "other",
];

const nullableCode = z.string().trim().min(1).max(64).nullable().optional();

export const jurisdictionSchema = z.object({
  id: z.string().uuid().optional(),
  countryCode: z.string().length(2).default("AR"),
  level: z.enum(jurisdictionLevels),
  provinceCode: nullableCode,
  municipalityCode: nullableCode,
  name: z.string().trim().min(1).max(255),
  metadata: z.record(z.string(), z.unknown()).default({}),
}).superRefine((value, context) => {
  if (value.level === "national" && (value.provinceCode || value.municipalityCode)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "National jurisdictions cannot include province or municipality" });
  }
  if (value.level === "provincial" && (!value.provinceCode || value.municipalityCode)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Provincial jurisdictions require province only" });
  }
  if (value.level === "municipal" && (!value.provinceCode || !value.municipalityCode)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Municipal jurisdictions require province and municipality" });
  }
});

export const sourceSchema = z.object({
  id: z.string().uuid().optional(),
  organization: z.string().trim().min(1).max(255),
  sourceType: z.enum(["official", "editorial", "court", "secondary", "other"]),
  title: z.string().trim().max(500).nullable().optional(),
  url: z.string().url().nullable().optional(),
  externalId: z.string().trim().max(255).nullable().optional(),
  retrievedAt: z.coerce.date().nullable().optional(),
  official: z.boolean().default(false),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

const optionalDate = z.coerce.date().nullable().optional();

export const normSchema = z.object({
  id: z.string().uuid().optional(),
  canonicalId: z.string().trim().min(1).max(255),
  jurisdictionId: z.string().uuid(),
  type: z.enum(normTypes),
  subtype: z.string().trim().max(100).nullable().optional(),
  number: z.string().trim().max(100).nullable().optional(),
  name: z.string().trim().min(1).max(500),
  shortName: z.string().trim().max(255).nullable().optional(),
  title: z.string().trim().max(1000).nullable().optional(),
  summary: z.string().nullable().optional(),
  status: z.enum(normStatuses).default("unknown"),
  sanctionedAt: optionalDate,
  promulgatedAt: optionalDate,
  publishedAt: optionalDate,
  effectiveFrom: optionalDate,
  effectiveTo: optionalDate,
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const legalUnitSchema = z.object({
  id: z.string().uuid().optional(),
  canonicalId: z.string().trim().min(1).max(255),
  normId: z.string().uuid(),
  parentId: z.string().uuid().nullable().optional(),
  type: z.enum(legalUnitTypes),
  number: z.string().trim().max(100).nullable().optional(),
  canonicalReference: z.string().trim().min(1).max(500),
  heading: z.string().trim().max(1000).nullable().optional(),
  hierarchy: z.record(z.string(), z.unknown()).default({}),
  sortOrder: z.number().int().nonnegative().default(0),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const legalUnitVersionSchema = z.object({
  id: z.string().uuid().optional(),
  unitId: z.string().uuid(),
  sourceId: z.string().uuid().nullable().optional(),
  versionLabel: z.string().trim().max(100).nullable().optional(),
  rawText: z.string().min(1),
  validFrom: optionalDate,
  validTo: optionalDate,
  isCurrent: z.boolean().default(true),
  official: z.boolean().default(false),
  metadata: z.record(z.string(), z.unknown()).default({}),
}).refine((value) => !value.validFrom || !value.validTo || value.validTo > value.validFrom, {
  message: "validTo must be later than validFrom",
  path: ["validTo"],
}).refine((value) => !value.isCurrent || !value.validTo, {
  message: "A current version cannot have validTo",
  path: ["validTo"],
});

export const normRelationshipSchema = z.object({
  id: z.string().uuid().optional(),
  sourceNormId: z.string().uuid(),
  sourceUnitId: z.string().uuid().nullable().optional(),
  targetNormId: z.string().uuid(),
  targetUnitId: z.string().uuid().nullable().optional(),
  relationshipType: z.enum(relationshipTypes),
  effectiveFrom: optionalDate,
  effectiveTo: optionalDate,
  description: z.string().nullable().optional(),
  sourceId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const bookSchema = z.object({
  id: z.string().uuid().optional(),
  canonicalId: z.string().trim().min(1).max(255),
  title: z.string().trim().min(1).max(1000),
  subtitle: z.string().trim().max(1000).nullable().optional(),
  authors: z.array(z.string().trim().min(1)).min(1),
  publisher: z.string().trim().max(255).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const bookEditionSchema = z.object({
  id: z.string().uuid().optional(),
  bookId: z.string().uuid(),
  editionLabel: z.string().trim().max(255).nullable().optional(),
  isbn: z.string().trim().max(32).nullable().optional(),
  publishedAt: optionalDate,
  language: z.string().trim().max(16).default("es"),
  sourceId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const bookPageSchema = z.object({
  id: z.string().uuid().optional(),
  editionId: z.string().uuid(),
  pageNumber: z.number().int().positive(),
  rawText: z.string(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const bookFragmentSchema = z.object({
  id: z.string().uuid().optional(),
  editionId: z.string().uuid(),
  pageId: z.string().uuid().nullable().optional(),
  startPage: z.number().int().positive(),
  endPage: z.number().int().positive(),
  startOffset: z.number().int().nonnegative(),
  endOffset: z.number().int().positive(),
  rawText: z.string().min(1),
  canonicalReference: z.string().trim().min(1).max(500),
  metadata: z.record(z.string(), z.unknown()).default({}),
}).refine((value) => value.endPage >= value.startPage && value.endOffset > value.startOffset, {
  message: "Fragment range is invalid",
});

export const embeddingReferenceSchema = z.object({
  id: z.string().uuid().optional(),
  entityType: z.enum(["legal_unit_version", "book_fragment", "court_decision_version"]),
  entityId: z.string().uuid(),
  provider: z.string().trim().default("pinecone"),
  indexName: z.string().trim().min(1),
  namespace: z.string().trim().nullable().optional(),
  vectorExternalId: z.string().trim().min(1),
  embeddingModel: z.string().trim().min(1),
  contentHash: z.string().trim().min(1),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const courtDecisionContractSchema = z.object({
  id: z.string().uuid().optional(),
  canonicalId: z.string().min(1),
  jurisdictionId: z.string().uuid(),
  court: z.string().min(1),
  chamber: z.string().nullable().optional(),
  caseName: z.string().min(1),
  docketNumber: z.string().nullable().optional(),
  cuij: z.string().trim().nullable().optional(),
  decisionType: z.string().trim().max(100).nullable().optional(),
  decidedAt: optionalDate,
  judges: z.array(z.string()).default([]),
  sourceId: z.string().uuid().nullable().optional(),
  summary: z.string().nullable().optional(),
  matters: z.array(z.string().trim().min(1)).default([]),
  keywords: z.array(z.string().trim().min(1)).default([]),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const courtDecisionSchema = courtDecisionContractSchema;

export const courtDecisionVersionSchema = z.object({
  id: z.string().uuid().optional(),
  decisionId: z.string().uuid(),
  sourceId: z.string().uuid().nullable().optional(),
  externalId: z.string().trim().min(1).max(255),
  versionLabel: z.string().trim().max(100).nullable().optional(),
  rawText: z.string().min(1),
  canonicalUrl: z.string().url().nullable().optional(),
  publishedAt: optionalDate,
  retrievedAt: z.coerce.date().nullable().optional(),
  isCurrent: z.boolean().default(true),
  official: z.boolean().default(false),
  metadata: z.record(z.string(), z.unknown()).default({}),
});
